import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Form, Steps, Button, Spin, Grid } from "antd";
import { ChevronLeft, ChevronRight, Send } from "lucide-react";
import dayjs from "dayjs";
import useAuthStore from "stores/AuthStore/AuthStore";
import {
  renderErrorNotifications,
  renderSuccessNotifications,
} from "helpers/error.helpers";
import {
  createPortalEvent,
  updatePortalEvent,
  getPortalEventDetail,
  initDraftPortalEvent,
  updateDraftPortalEvent,
  getDraftPortalEvent,
} from "services/portalEvents.service";
import useEventFormData from "hooks/useEventFormData/useEventFormData";
import {
  buildEventPayload,
  mapEventDetailToFormValues,
  serializeDraftFormValues,
  deserializeDraftFormValues,
  isShownInListings,
  SHOW_IN_LISTINGS_COPY,
} from "../../../commons/events.helpers";
import Step1Details from "./Step1Details";
import Step2Location from "./Step2Location";
import Step3Content from "./Step3Content";
import Step4Tickets from "./Step4Tickets";
import Step5Media from "./Step5Media";
import Step6Review from "./Step6Review";
import PosterProvenance from "./_blocks/PosterProvenance";

const STEPS = [
  { title: "Event Details" },
  { title: "Date & Location" },
  { title: "Content" },
  { title: "Tickets" },
  { title: "Media" },
  { title: "Review" },
];

const STEP_FIELDS = [
  [
    "event_type",
    "title",
    "description",
    "event_access",
    "tags",
    "show_in_listings",
  ],
  [
    "start_date_time",
    "end_date_time",
    "location_mode",
    "location_id",
    ["location", "name"],
    ["location", "address_line_1"],
    ["location", "city"],
    ["location", "state"],
    ["location", "postal_code"],
    ["location", "country_code"],
    ["location", "geo_location", "lat"],
    ["location", "geo_location", "lng"],
  ],
  [
    "organiser_name",
    "organiser_logo_url",
    "activities",
    "amenities",
    "schedule_info_validator",
  ],
  ["contact_email", "contact_phone", "tickets_validator"],
  ["media_validator"],
  [],
];

/**
 * @param {string}  [eventId]      - When present, puts the form into edit mode for that event.
 * @param {object}  [seedValues]   - When present (clone mode), pre-fills the form with these
 *                                   values on mount without entering edit mode.
 * @param {string}  [title]        - Overrides the heading. Optional.
 * @param {React.ReactNode} [intro] - Optional content rendered above the form (provenance, etc.).
 *
 * The "create from poster" flow navigates here with router state
 * { posterSeed, posterProvenance } — posterSeed seeds the form, posterProvenance
 * renders the assistant summary above it.
 */
function CreateEvent({ eventId, seedValues, title, intro } = {}) {
  const [form] = Form.useForm();
  const { xs } = Grid.useBreakpoint();
  const { userData, isRootUser } = useAuthStore();
  const navigate = useNavigate();
  const { posterSeed, posterProvenance } = useLocation().state ?? {};
  const effectiveSeed = seedValues ?? posterSeed;
  const isEditMode = Boolean(eventId);
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingEvent, setLoadingEvent] = useState(isEditMode);
  const [isPublished, setIsPublished] = useState(false);
  // The platform fee switch can change only while the event is a draft
  // (IN_PROGRESS / DRAFT). Core also refuses it once the event has been live
  // or has bookings, and returns a message saying so.
  const [platformFeeLocked, setPlatformFeeLocked] = useState(false);
  // True once a waived draft was switched back on because this vendor can no
  // longer waive the fee (core would switch it on at publish anyway).
  const [platformFeeReset, setPlatformFeeReset] = useState(false);
  const [showTC, setShowTC] = useState(false);
  const [draftEventId, setDraftEventId] = useState(null);
  const originalTicketsRef = useRef([]);
  // Listing flag as loaded for a published edit, to tell the partner about the
  // listing delay only when it actually changed.
  const originalShowInListingsRef = useRef(null);

  const {
    communities,
    amenities,
    activities,
    locations,
    canWaivePlatformFee,
    loading: loadingData,
  } = useEventFormData();

  const loadEventForEdit = useCallback(async () => {
    if (!isEditMode) return;
    setLoadingEvent(true);
    const { data, errors } = await getPortalEventDetail(eventId);
    if (errors?.length > 0) {
      renderErrorNotifications(errors);
      navigate("/shoup-community/events");
      return;
    }
    if (data.publication_status === "PUBLISHED") {
      setIsPublished(true);
      originalTicketsRef.current = data.tickets ?? [];
      originalShowInListingsRef.current = isShownInListings(
        data.show_in_listings
      );
    }
    setPlatformFeeLocked(
      !["IN_PROGRESS", "DRAFT"].includes(data.publication_status)
    );

    // Check for a wizard draft record — if present, restore form from it
    const { data: draft } =
      data.publication_status === "IN_PROGRESS"
        ? await getDraftPortalEvent(eventId)
        : { data: null, errors: [] };
    if (draft) {
      form.setFieldsValue(deserializeDraftFormValues(draft.data));
      setCurrentStep(draft.current_step);
      setDraftEventId(eventId);
      if (draft.data?.tc_title) setShowTC(true);
    } else {
      form.setFieldsValue(mapEventDetailToFormValues(data));
    }

    setLoadingEvent(false);
  }, [eventId, isEditMode, form, navigate]);

  useEffect(() => {
    loadEventForEdit();
  }, [loadEventForEdit]);

  // A waived draft (loaded, restored, cloned or seeded) whose vendor can no
  // longer waive the fee goes back to charging it, so what the organiser
  // reviews, saves and publishes matches what core will do. Only on a definite
  // "not allowed": an unknown permission never changes the value.
  useEffect(() => {
    if (loadingData || loadingEvent) return;
    if (canWaivePlatformFee !== false || platformFeeLocked || isPublished)
      return;
    if (form.getFieldValue("charge_platform_fee") === false) {
      form.setFieldValue("charge_platform_fee", true);
      setPlatformFeeReset(true);
    }
  }, [
    loadingData,
    loadingEvent,
    canWaivePlatformFee,
    platformFeeLocked,
    isPublished,
    form,
  ]);

  // Seed clone / poster values once the form is mounted (create mode only)
  useEffect(() => {
    if (!isEditMode && effectiveSeed) {
      form.setFieldsValue(effectiveSeed);
      if (effectiveSeed.tc_title) setShowTC(true);
    }
    // Run only on mount — the seed reference is stable (built once before navigation)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToFirstError = (errorInfo) => {
    const first = errorInfo?.errorFields?.[0]?.name;
    if (!first) return;
    try {
      form.scrollToField(first, { behavior: "smooth", block: "center" });
    } catch {
      // Fallback: scrollToField can fail for custom components (MediaUpload etc.)
    }
    setTimeout(() => {
      const errEl = document.querySelector(".ant-form-item-has-error");
      if (errEl) errEl.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  const handleNext = async () => {
    try {
      const fields = [...STEP_FIELDS[currentStep]];

      if (currentStep === 0) {
        if (form.getFieldValue("event_access") === "MEMBERS_ONLY") {
          if (!form.getFieldValue("community_id")) {
            form.setFields([
              {
                name: "community_id",
                errors: ["Community is required for Members Only events"],
              },
            ]);
            form.scrollToField("community_id", {
              behavior: "smooth",
              block: "center",
            });
            return;
          }
        } else {
          form.setFields([{ name: "community_id", errors: [] }]);
        }
      }

      if (currentStep === 2) {
        const scheduleItems = form.getFieldValue("schedule_info") ?? [];
        scheduleItems.forEach((_, i) => {
          fields.push(
            ["schedule_info", i, "date"],
            ["schedule_info", i, "name"],
            ["schedule_info", i, "value"]
          );
        });

        if (showTC) {
          fields.push("tc_title", "tc_subtitle", "tc_sections_validator");
          const tcSections = form.getFieldValue("tc_sections") ?? [];
          tcSections.forEach((_, i) => {
            fields.push(
              ["tc_sections", i, "heading"],
              ["tc_sections", i, "content"]
            );
          });
        }
      }

      if (currentStep === 3) {
        const tickets = form.getFieldValue("tickets") ?? [];
        tickets.forEach((_, i) => {
          fields.push(
            ["tickets", i, "name"],
            ["tickets", i, "price"],
            ["tickets", i, "quantity"]
          );
        });
      }

      await form.validateFields(fields.length ? fields : undefined);

      setSaving(true);

      const values = form.getFieldsValue(true);

      if (currentStep === 0 && !draftEventId && !isEditMode) {
        // Step 1 in fresh create mode: init the draft on the backend
        const { data, errors } = await initDraftPortalEvent({
          title: values.title,
          description: values.description,
          event_type: values.event_type,
          event_access: values.event_access,
          tags: values.tags ?? [],
          show_in_listings: values.show_in_listings ?? true,
          ...(values.community_id && { community_id: values.community_id }),
        });
        if (errors?.length > 0) {
          renderErrorNotifications(errors);
          setSaving(false);
          return;
        }
        await updateDraftPortalEvent(data.event_id, {
          data: serializeDraftFormValues(values),
          current_step: currentStep + 1,
        });
        setDraftEventId(data.event_id);
      } else if (draftEventId) {
        // Only IN_PROGRESS events have a draftEventId — DRAFT/PUBLISHED edits skip this
        const activeEventId = draftEventId;
        const { errors } = await updateDraftPortalEvent(activeEventId, {
          data: serializeDraftFormValues(values),
          current_step: currentStep + 1,
        });
        if (errors?.length > 0) {
          // Non-blocking — show notification but let the user continue
          renderErrorNotifications(errors);
        }
      }

      setSaving(false);
      setCurrentStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (errorInfo) {
      setSaving(false);
      scrollToFirstError(errorInfo);
    }
  };

  const handleBack = () => {
    if (currentStep === 0) navigate("/shoup-community/events");
    else {
      setCurrentStep((s) => s - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      setSubmitting(true);
      const values = form.getFieldsValue(true);

      // Sync location contact details from the Step 4 event contact fields
      if (values.location && values.location_mode === "new") {
        values.location = {
          ...values.location,
          contact_details: {
            email: values.contact_email,
            phone_number: values.contact_phone,
          },
          operating_hours: {
            opening_time: dayjs("04:00", "HH:mm"),
            closing_time: dayjs("23:00", "HH:mm"),
            days_of_week: [
              "MONDAY",
              "TUESDAY",
              "WEDNESDAY",
              "THURSDAY",
              "FRIDAY",
              "SATURDAY",
              "SUNDAY",
            ],
          },
        };
      }

      if (
        !isPublished &&
        !isEditMode &&
        !draftEventId &&
        values.location_mode === "new"
      ) {
        const newLat = values.location?.geo_location?.lat;
        const newLng = values.location?.geo_location?.lng;
        if (newLat && newLng) {
          const duplicate = locations.find(
            (loc) =>
              loc.geo_location?.lat === newLat &&
              loc.geo_location?.lng === newLng
          );
          if (duplicate) {
            setSubmitting(false);
            renderErrorNotifications([
              {
                message: `A venue at these coordinates already exists: "${duplicate.location_name}" (${[duplicate.address_line_1, duplicate.city].filter(Boolean).join(", ")}). Please go back to Step 2 and select it from the existing venue list instead.`,
              },
            ]);
            return;
          }
        }
      }

      // One unified full-payload path for draft, in-progress, and published
      // events. The core diffs published events and applies only what changed
      // (price, inventory increase, dates, details, new tiers).
      const payload = buildEventPayload(
        values,
        userData,
        !!(eventId || draftEventId)
      );
      const activeEventId = draftEventId || eventId;

      const { errors } = activeEventId
        ? await updatePortalEvent(activeEventId, payload)
        : await createPortalEvent(payload);

      if (errors?.length > 0) {
        renderErrorNotifications(errors);
        return;
      }

      const listingChanged =
        isPublished &&
        originalShowInListingsRef.current !== null &&
        originalShowInListingsRef.current !== payload.show_in_listings;

      renderSuccessNotifications({
        title: activeEventId ? "Event Updated!" : "Event Created!",
        message: activeEventId
          ? `"${payload.title}" has been updated.${
              listingChanged
                ? ` ${SHOW_IN_LISTINGS_COPY.saveListingChanged}`
                : ""
            }`
          : `"${payload.title}" has been saved as a draft. Open it to publish.`,
      });

      navigate("/shoup-community/events");
    } catch (errorInfo) {
      scrollToFirstError(errorInfo);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <Button
          type="text"
          icon={<ChevronLeft className="h-4 w-4" />}
          onClick={handleBack}
        >
          {currentStep === 0 ? "Back to Events" : "Previous"}
        </Button>
        <span className="text-sm text-muted-foreground">
          Step {currentStep + 1} of {STEPS.length}
        </span>
      </div>

      <h1 className="text-2xl font-bold">
        {title
          ? title
          : isEditMode
            ? "Edit Event"
            : posterProvenance
              ? "Create event from poster"
              : seedValues
                ? "Clone Event"
                : "Create New Event"}
      </h1>

      {posterProvenance ? <PosterProvenance {...posterProvenance} /> : intro}

      <Steps
        current={currentStep}
        items={STEPS}
        size="small"
        className="mb-6"
        responsive={false}
        labelPlacement={xs ? "vertical" : "horizontal"}
      />

      {loadingEvent ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <Spin size="large" tip="Loading event…" />
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            ...(!isRootUser && { event_access: "PUBLIC" }),
            // Customers pay the platform fee unless an allowed vendor
            // switches it off. Drafts saved before this field existed keep it.
            charge_platform_fee: true,
            // "Show in listings" defaults to ON. Set here (not on the
            // Form.Item) so it registers even when the wizard resumes at a
            // later step and Step 1 never mounts.
            show_in_listings: true,
          }}
          scrollToFirstError={{ behavior: "smooth", block: "center" }}
        >
          {isPublished && (
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 mb-6 text-sm text-blue-800">
              Once published, the venue can't be changed, and existing tickets
              can&apos;t be renamed, removed, or reduced.
            </div>
          )}
          <div className="rounded-2xl border bg-card p-2 shadow-sm min-h-[400px]">
            {currentStep === 0 && (
              <Step1Details
                communities={communities}
                loadingData={loadingData}
                isPublished={isPublished}
              />
            )}
            {currentStep === 1 && (
              <Step2Location
                form={form}
                locations={locations}
                amenities={amenities}
                loadingData={loadingData}
                isPublished={isPublished}
              />
            )}
            {currentStep === 2 && (
              <Step3Content
                amenities={amenities}
                activities={activities}
                loadingData={loadingData}
                form={form}
                showTC={showTC}
                setShowTC={setShowTC}
                isPublished={isPublished}
              />
            )}
            {currentStep === 3 && (
              <Step4Tickets
                form={form}
                isPublished={isPublished}
                originalTickets={originalTicketsRef.current}
                canWaivePlatformFee={canWaivePlatformFee === true}
                platformFeeLocked={platformFeeLocked}
                platformFeeReset={platformFeeReset}
              />
            )}
            {currentStep === 4 && (
              <Step5Media form={form} isPublished={isPublished} />
            )}
            {currentStep === 5 && (
              <Step6Review
                form={form}
                locations={locations}
                canWaivePlatformFee={canWaivePlatformFee === true}
                platformFeeReset={platformFeeReset}
              />
            )}
          </div>

          <div className="flex justify-between mt-6">
            <Button
              type="text"
              icon={<ChevronLeft className="h-4 w-4" />}
              onClick={handleBack}
              disabled={submitting || saving}
            >
              {currentStep === 0 ? "Cancel" : "Previous"}
            </Button>
            {currentStep < STEPS.length - 1 ? (
              <Button type="primary" loading={saving} onClick={handleNext}>
                {draftEventId ? "Save & Next" : "Next"}{" "}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                type="primary"
                loading={submitting}
                icon={<Send className="h-4 w-4" />}
                onClick={handleSubmit}
              >
                {isEditMode && !draftEventId ? "Update Event" : "Submit Event"}
              </Button>
            )}
          </div>
        </Form>
      )}
    </div>
  );
}

export default CreateEvent;
